/**
 * Journey Analytics - browser tracker (Phase B).
 *
 * Sends anonymous journey events to the collector POST /journey/event
 * (App\Http\Controllers\JourneyAnalyticsCollectController). The journey identity lives only
 * in the HttpOnly upc_aj cookie set by the collector - this file never reads or stores it.
 *
 * Enabled only when Blade renders <script type="application/json" id="journey-analytics-config">
 * (resources/views/includes/site-scripts.blade.php, only with ANALYTICS_ENABLED). Without it,
 * every call is a no-op. No extra HTTP request is needed to find out.
 *
 * API
 *   import { JourneyAnalytics } from './journey-analytics.js';   // ES modules (app.js, ...)
 *   window.JourneyAnalytics.track(...)                           // inline Blade / lazy-loaded block scripts
 *
 *   JourneyAnalytics.track('cta_click', { cta_id: 'internet-order', section: 'internet-offer' });
 *   -> true when queued, false when disabled / unknown event. Never throws.
 *
 * Queue lifecycle
 *   1. init: page_view is queued and sent at once.
 *   2. handshake: until the first 2xx response, at most ONE request is in flight (fetch, not a
 *      beacon: we must see the response that carries Set-Cookie: upc_aj). Events tracked meanwhile
 *      wait in the queue. Parallel first requests without the cookie would create separate
 *      journeys on the server (see the Phase A MySQL tests).
 *   3. ready: queued events are flushed right away, afterwards every FLUSH_INTERVAL (1 s after the
 *      first queued event), immediately when MAX_BATCH (20) events are waiting or a navigating CTA
 *      was clicked, and via navigator.sendBeacon on pagehide / visibilitychange=hidden.
 *   Retries (network error, 429, 5xx) resend the same event objects, i.e. the same event_uuid;
 *   the server ignores duplicates. 4xx other than 408/429 drops the batch (never valid). 204 means
 *   the server has analytics switched off: the tracker stops.
 *
 * Data: only the explicitly mapped fields below leave the browser. No innerText, no input values,
 * no FormData, no generic dataset dump. Metadata keys are limited to the per-event schema that
 * Blade passes from config('journey-analytics.collector.events'); the server enforces it again.
 */

const GLOBAL_KEY = Symbol.for('upc.journeyAnalytics');
const CONFIG_ELEMENT_ID = 'journey-analytics-config';

const EVENT_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
// Same character set as the collector's `element` field.
const ID_PATTERN = /^[A-Za-z0-9_\-:.]{1,100}$/;
const MAX_STRING = 100;

// Existing business add-to-cart attributes ("cid-pid[-type]|..."): read for product_interaction.
const PRODUCT_ATTRIBUTES = ['data-add-to-cart', 'data-add-to-cart-package', 'data-add-to-cart-items', 'data-add-to-cart-interest', 'data-add-to-cart-bundle'];
const PRODUCT_SELECTOR = PRODUCT_ATTRIBUTES.map((name) => `[${name}]`).join(',');
const PRODUCT_ITEM_PATTERN = /^(\d{1,3})-(\d{1,7})(?:-[A-Za-z_]{1,20})?$/;
// product_interaction.action enum (H2.1) and its existing UI sources (stable ids only).
const PRODUCT_ACTIONS = ['select', 'deselect', 'replace'];
const PRODUCT_REMOVE_SELECTOR = '.remove-cart-item[data-remove-cart-item]';
const SERVICE_CHECKBOX_SELECTOR = 'input[type="checkbox"][data-service-pid][data-service-cid]';
// page_view.navigation_type enum (H2.1), PerformanceNavigationTiming.type ("prerender" is not reported).
const NAVIGATION_TYPES = ['navigate', 'reload', 'back_forward'];
const CHECKOUT_STARTED_KEY = 'ja_checkout_started';

const LEAD_FORM_SELECTOR = 'form[data-analytics-lead-form]';
// Input names reported by lead_form_validation_error (anything else is "other"); never values.
const LEAD_ERROR_FIELDS = [
    'name', 'lastname', 'email', 'phone', 'bill_address', 'bill_address_number', 'bill_city', 'bill_zip',
    'message', 'info', 'mgm_vs', 'customer_type',
];

export const DEFAULT_SETTINGS = Object.freeze({
    flushInterval: 1000,
    maxBatch: 20,
    maxBatchBytes: 14000, // collector limit is 16 KB, keepalive/beacon limit 64 KB
    maxQueue: 100,
    retryDelays: [1000, 3000, 10000],
    clickDedupeMs: 500,
    triggerWindowMs: 3000,
    closeInteractionMs: 1000,
});

/**
 * RFC 4122 v4 UUID. crypto.randomUUID needs a secure context (HTTPS / localhost) and a recent
 * browser (Safari 15.4+); getRandomValues covers the rest.
 */
export function generateEventUuid(cryptoObject = globalThis.crypto) {
    if (cryptoObject && typeof cryptoObject.randomUUID === 'function') {
        try {
            return cryptoObject.randomUUID();
        } catch (e) {
            // insecure context in some browsers - fall through
        }
    }

    const bytes = new Uint8Array(16);

    if (cryptoObject && typeof cryptoObject.getRandomValues === 'function') {
        cryptoObject.getRandomValues(bytes);
    } else {
        for (let i = 0; i < 16; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function cleanId(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();

    return ID_PATTERN.test(trimmed) ? trimmed : null;
}

function cleanString(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed.slice(0, MAX_STRING);
}

function createNoopTracker() {
    return {
        enabled: false,
        start() {},
        track() {
            return false;
        },
        flush() {},
        attachJQuery() {
            return false;
        },
        leadFormSubmitted() {
            return {};
        },
        scanLeadForms() {},
        state() {
            return { enabled: false, handshakeDone: false, queued: 0, inflight: 0 };
        },
    };
}

/**
 * @param {object|null} config  parsed #journey-analytics-config: { enabled, endpoint, events: {name: [keys]}, page: {type, id} }
 * @param {object} env          { window, settings } - injectable for tests
 */
export function createJourneyTracker(config, env = {}) {
    const win = env.window || globalThis.window;

    if (!win || !config || config.enabled !== true || typeof config.endpoint !== 'string' || !config.events) {
        return createNoopTracker();
    }

    const doc = win.document;
    const settings = { ...DEFAULT_SETTINGS, ...(env.settings || {}) };
    const schemas = config.events;

    const queue = [];
    let inflight = 0;
    let handshakeDone = false;
    let stopped = false;
    let started = false;
    let timer = null;

    let boundJQuery = null;
    let lastUserGesture = null;
    const seenLeadForms = new WeakSet();
    const startedLeadForms = new WeakSet();
    const leadErrorsSeen = new WeakMap(); // form -> Set("field:reason")
    let leadObserver = null;

    let lastClick = null; // { time, key } for double-click protection
    let lastTrigger = null; // { time, id } analytics id of the last clicked element (modal trigger)
    let lastCloseInteraction = null; // { time, kind }
    const modalOpenedAt = new WeakMap();

    function now() {
        return Date.now();
    }

    // ------------------------------------------------------------ event building

    function filterMetadata(event, metadata) {
        const allowed = Array.isArray(schemas[event]) ? schemas[event] : [];
        const clean = {};

        if (!metadata || typeof metadata !== 'object') {
            return clean;
        }

        for (const key of allowed) {
            if (!Object.prototype.hasOwnProperty.call(metadata, key)) {
                continue;
            }

            const value = metadata[key];

            if (typeof value === 'string') {
                const text = cleanString(value);

                if (text !== null) {
                    clean[key] = text;
                }
            } else if ((typeof value === 'number' && Number.isFinite(value)) || typeof value === 'boolean') {
                clean[key] = value;
            } else if (Array.isArray(value)) {
                clean[key] = value
                    .filter((item) => typeof item === 'string' || (typeof item === 'number' && Number.isFinite(item)))
                    .slice(0, 10)
                    .map((item) => (typeof item === 'string' ? item.slice(0, MAX_STRING) : item));
            }
        }

        return clean;
    }

    function track(event, metadata = {}, options = {}) {
        try {
            if (stopped || typeof event !== 'string' || !EVENT_PATTERN.test(event)
                || !Object.prototype.hasOwnProperty.call(schemas, event)) {
                return false;
            }

            if (queue.length >= settings.maxQueue) {
                return false;
            }

            const item = {
                event,
                event_uuid: generateEventUuid(win.crypto),
                page_url: String(win.location.href),
                occurred_at: now(),
                attempts: 0,
            };

            const element = cleanId(options.element);

            if (element !== null) {
                item.element = element;
            }

            if (options.referrer && typeof doc.referrer === 'string' && doc.referrer !== '') {
                item.referrer = doc.referrer;
            }

            const clean = filterMetadata(event, metadata);

            if (Object.keys(clean).length > 0) {
                item.metadata = clean;
            }

            queue.push(item);
            schedule(options.priority === true);

            return true;
        } catch (e) {
            return false;
        }
    }

    // ------------------------------------------------------------ sending

    function payloadOf(events) {
        // Explicit whitelist: internal fields (attempts) never leave the browser.
        return JSON.stringify({
            events: events.map((item) => {
                const out = { event: item.event, event_uuid: item.event_uuid, page_url: item.page_url, occurred_at: item.occurred_at };

                for (const key of ['referrer', 'element', 'metadata']) {
                    if (item[key] !== undefined) {
                        out[key] = item[key];
                    }
                }

                return out;
            }),
        });
    }

    function takeBatch() {
        const batch = [];

        while (queue.length > 0 && batch.length < settings.maxBatch) {
            batch.push(queue[0]);

            if (batch.length > 1 && payloadOf(batch).length > settings.maxBatchBytes) {
                batch.pop();
                break;
            }

            queue.shift();
        }

        return batch;
    }

    function clearTimer() {
        if (timer !== null) {
            win.clearTimeout(timer);
            timer = null;
        }
    }

    function schedule(immediate) {
        if (stopped) {
            return;
        }

        if (immediate || queue.length >= settings.maxBatch) {
            pump();

            return;
        }

        if (timer === null) {
            timer = win.setTimeout(() => {
                timer = null;
                pump();
            }, settings.flushInterval);
        }
    }

    function pump() {
        clearTimer();

        if (stopped) {
            return;
        }

        // Handshake: one request at a time until the first 2xx (cookie is set by then).
        while (queue.length > 0 && (handshakeDone || inflight === 0)) {
            const batch = takeBatch();

            if (batch.length === 0) {
                break;
            }

            send(batch);
        }
    }

    function send(batch) {
        inflight++;

        let request;

        try {
            request = win.fetch(config.endpoint, {
                method: 'POST',
                keepalive: true,
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: payloadOf(batch),
            });
        } catch (e) {
            request = Promise.reject(e);
        }

        Promise.resolve(request)
            .then((response) => outcomeOf(response.status), () => 'retry')
            .then((outcome) => {
                inflight--;
                handle(outcome, batch);
            })
            .catch(() => {
                // never surface into the page
            });
    }

    function outcomeOf(status) {
        if (status === 204) {
            return 'disabled';
        }

        if (status >= 200 && status < 300) {
            return 'ok';
        }

        if (status === 408 || status === 429 || status >= 500) {
            return 'retry';
        }

        return 'drop';
    }

    function handle(outcome, batch) {
        if (outcome === 'disabled') {
            stopped = true;
            queue.length = 0;
            clearTimer();

            return;
        }

        if (outcome === 'ok') {
            handshakeDone = true;
        }

        if (outcome === 'retry') {
            const attempt = Math.max(...batch.map((item) => item.attempts)) + 1;

            if (attempt <= settings.retryDelays.length) {
                // Same objects -> same event_uuid on the resend.
                batch.forEach((item) => {
                    item.attempts = attempt;
                });
                queue.unshift(...batch);
                clearTimer();
                timer = win.setTimeout(() => {
                    timer = null;
                    pump();
                }, settings.retryDelays[attempt - 1]);

                return;
            }
        }

        if (queue.length > 0) {
            // After the handshake everything that waited goes out at once.
            outcome === 'ok' ? pump() : schedule(false);
        }
    }

    /**
     * Page is going away (or hidden): hand the queue to sendBeacon, which survives unload.
     * While the handshake request is still in flight nothing is sent - a parallel request without
     * the cookie would open a second journey; those events stay queued (and go out normally if
     * the page comes back from the background).
     */
    function flushOnExit() {
        try {
            if (stopped || queue.length === 0 || (!handshakeDone && inflight > 0)) {
                return;
            }

            clearTimer();

            while (queue.length > 0) {
                const batch = takeBatch();

                if (batch.length === 0) {
                    break;
                }

                const body = payloadOf(batch);
                let queued = false;

                if (win.navigator && typeof win.navigator.sendBeacon === 'function') {
                    try {
                        // text/plain: CORS-safelisted everywhere; the collector parses it as JSON.
                        queued = win.navigator.sendBeacon(config.endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
                    } catch (e) {
                        queued = false;
                    }
                }

                if (!queued) {
                    send(batch);
                }

                if (!handshakeDone) {
                    break; // pre-handshake: a single request only
                }
            }
        } catch (e) {
            // ignore
        }
    }

    // ------------------------------------------------------------ declarative CTA tracking

    function destinationOf(element) {
        const explicit = cleanString(element.getAttribute('data-analytics-destination'));

        if (explicit !== null) {
            return explicit;
        }

        const href = element.tagName === 'A' ? element.getAttribute('href') : null;

        if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(href)) {
            return null;
        }

        try {
            const url = new URL(href, win.location.href);

            // Path only (no query / fragment): destination is a stable id, not a URL dump.
            return url.origin === win.location.origin ? url.pathname.slice(0, MAX_STRING) : `external:${url.hostname}`.slice(0, MAX_STRING);
        } catch (e) {
            return null;
        }
    }

    function isNavigating(element, event) {
        if (element.tagName !== 'A' || event.defaultPrevented) {
            return false;
        }

        const href = element.getAttribute('href') || '';
        const target = (element.getAttribute('target') || '').toLowerCase();

        return href !== '' && href.charAt(0) !== '#' && !/^javascript:/i.test(href) && target !== '_blank'
            && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0;
    }

    function onClick(event) {
        try {
            const target = event.target instanceof win.Element ? event.target : null;

            if (target === null) {
                return;
            }

            rememberCloseInteraction(target);

            const product = target.closest(PRODUCT_SELECTOR);
            const removal = product === null ? target.closest(PRODUCT_REMOVE_SELECTOR) : null;

            if (product !== null) {
                trackProductInteraction(product);
            } else if (removal !== null) {
                trackProductRemoval(removal);
            }

            const element = target.closest('[data-analytics-event]');

            if (element === null) {
                return;
            }

            const id = cleanId(element.getAttribute('data-analytics-id'));
            const name = element.getAttribute('data-analytics-event');

            if (id === null || !EVENT_PATTERN.test(name || '')) {
                return;
            }

            lastTrigger = { time: now(), id };

            const key = `${name}:${id}`;

            if (lastClick !== null && lastClick.key === key && now() - lastClick.time < settings.clickDedupeMs) {
                return; // double click
            }

            lastClick = { time: now(), key };

            // Explicit mapping - no dataset dump, no innerText.
            track(name, {
                cta_id: id,
                section: element.getAttribute('data-analytics-section') || undefined,
                destination: destinationOf(element) || undefined,
                placement: element.getAttribute('data-analytics-placement') || undefined,
            }, {
                element: id,
                // A navigating link: send now with keepalive (never delays or blocks the navigation).
                priority: isNavigating(element, event),
            });
        } catch (e) {
            // ignore
        }
    }

    // ------------------------------------------------------------ cart / checkout (Phase C3)

    /**
     * product_interaction (H2.1 action enum, collector schema "enum:select|deselect|replace"):
     *   select    click on an element with an add-to-cart attribute, or a service checkbox checked
     *   replace   the checkout "Replace" button (add-to-cart attribute AND data-remove-cart-item)
     *   deselect  a cart item's remove control (.remove-cart-item[data-remove-cart-item]) or a
     *             service checkbox unchecked
     * Only the internal "cid-pid" pairs / data-service-pid|cid ids are read (category class from the
     * Blade config); never product names (checkbox values), prices or texts from the DOM. The
     * authoritative cart change is the server's cart_item_added.
     */
    function trackProductInteraction(element) {
        sendProductInteraction(element.hasAttribute('data-remove-cart-item') ? 'replace' : 'select', productItems(element), element);
    }

    function trackProductRemoval(element) {
        const pid = String(element.getAttribute('data-remove-cart-item') || '').trim();

        if (/^\d{1,7}$/.test(pid)) {
            sendProductInteraction('deselect', { pids: [Number(pid)], categories: [] }, element);
        }
    }

    /** Service selector checkbox (data-service-pid / data-service-cid): checked = select, unchecked = deselect. */
    function onProductToggle(event) {
        try {
            const input = event.target instanceof win.Element ? event.target : null;

            if (input === null || !input.matches(SERVICE_CHECKBOX_SELECTOR)) {
                return;
            }

            const pid = String(input.getAttribute('data-service-pid') || '');
            const cid = String(input.getAttribute('data-service-cid') || '');
            const category = (config.product_categories || {})[cid];

            if (/^\d{1,7}$/.test(pid) && /^\d{1,3}$/.test(cid) && category) {
                sendProductInteraction(input.checked ? 'select' : 'deselect', { pids: [Number(pid)], categories: [category] }, input);
            }
        } catch (e) {
            // ignore
        }
    }

    function sendProductInteraction(action, items, element) {
        if (!PRODUCT_ACTIONS.includes(action) || items.pids.length === 0) {
            return;
        }

        const key = `product:${action}:${items.pids.join(',')}`;

        if (lastClick !== null && lastClick.key === key && now() - lastClick.time < settings.clickDedupeMs) {
            return;
        }

        lastClick = { time: now(), key };

        const page = config.page || {};

        track('product_interaction', {
            action,
            categories: items.categories,
            pids: items.pids,
            placement: cleanId(element.getAttribute('data-analytics-placement') || '') || (typeof page.type === 'string' ? page.type : undefined),
        }, { priority: true });
    }

    function productItems(element) {
        const classes = config.product_categories || {};
        const pids = [];
        const categories = [];

        PRODUCT_ATTRIBUTES.forEach((name) => {
            const value = element.getAttribute(name);

            if (!value) {
                return;
            }

            value.split('|').slice(0, 10).forEach((entry) => {
                const match = PRODUCT_ITEM_PATTERN.exec(entry.trim());
                const category = match ? classes[match[1]] : undefined;

                // Unknown categories (e.g. 0 = gift) are not product interactions.
                if (match && category && !pids.includes(Number(match[2]))) {
                    pids.push(Number(match[2]));

                    if (!categories.includes(category)) {
                        categories.push(category);
                    }
                }
            });
        });

        return { pids, categories };
    }

    function sessionFlag(action) {
        try {
            if (action === 'get') {
                return win.sessionStorage.getItem(CHECKOUT_STARTED_KEY);
            }

            if (action === 'set') {
                win.sessionStorage.setItem(CHECKOUT_STARTED_KEY, '1');
            } else {
                win.sessionStorage.removeItem(CHECKOUT_STARTED_KEY);
            }
        } catch (e) {
            // storage unavailable: checkout_started may repeat, nothing else breaks
        }

        return null;
    }

    /**
     * navigate | reload | back_forward from the Navigation Timing Level 2 entry; undefined when the API
     * (or the entry) is not available - no deprecated performance.navigation fallback.
     */
    function navigationType() {
        try {
            const entry = win.performance && typeof win.performance.getEntriesByType === 'function'
                ? win.performance.getEntriesByType('navigation')[0]
                : null;

            return entry && NAVIGATION_TYPES.includes(entry.type) ? entry.type : undefined;
        } catch (e) {
            return undefined;
        }
    }

    function isReload() {
        return navigationType() === 'reload';
    }

    /**
     * checkout_step_view for the step marker rendered by resources/views/order/layout.blade.php, and
     * checkout_started for the first step view of a checkout run (sessionStorage flag, not identity):
     * a fresh add-to-cart redirect (entry add_to_cart, not a reload) starts a new run, the complete
     * step ends it. Mirrors when the existing GTM op_started fires.
     */
    function trackCheckoutStep() {
        try {
            const marker = doc.querySelector('[data-analytics-checkout-step]');

            if (marker === null) {
                return;
            }

            const step = cleanId(marker.getAttribute('data-analytics-checkout-step') || '');
            const index = parseInt(marker.getAttribute('data-analytics-checkout-step-index') || '0', 10);
            const entry = cleanId(marker.getAttribute('data-analytics-checkout-entry') || '') || 'resume';

            if (step === null) {
                return;
            }

            const metadata = { step, step_index: Number.isFinite(index) ? index : 0 };

            track('checkout_step_view', metadata);

            if (step === 'complete') {
                sessionFlag('remove');

                return;
            }

            if (sessionFlag('get') === null || (entry === 'add_to_cart' && !isReload())) {
                sessionFlag('set');
                track('checkout_started', { ...metadata, entry_point: entry });
            }
        } catch (e) {
            // ignore
        }
    }

    /**
     * Declarative submit events: a form with data-analytics-submit-event="<event>" tracks that event
     * when it is really submitted (its own validation did not call preventDefault - this listener runs
     * in the bubble phase on document, after the form's handlers). Metadata: only the keys of the
     * event's schema, each read from data-analytics-<key with dashes>; never a field value.
     */
    function onSubmit(event) {
        try {
            const form = event.target instanceof win.Element ? event.target : null;

            if (form === null || event.defaultPrevented || !form.matches('form[data-analytics-submit-event]')) {
                return;
            }

            const name = form.getAttribute('data-analytics-submit-event') || '';
            const keys = Array.isArray(schemas[name]) ? schemas[name] : null;

            if (keys === null) {
                return;
            }

            const metadata = {};

            keys.forEach((key) => {
                const value = form.getAttribute(`data-analytics-${key.replace(/_/g, '-')}`);

                if (value !== null && cleanId(value) !== null) {
                    metadata[key] = /^\d{1,9}$/.test(value) ? Number(value) : cleanId(value);
                }
            });

            // A submit navigates away: send right away (keepalive), the pagehide beacon covers the rest.
            track(name, metadata, { priority: true });
        } catch (e) {
            // ignore
        }
    }

    // ------------------------------------------------------------ lead forms (Phase C2)

    /**
     * Forms marked with data-analytics-lead-* (App\Services\JourneyAnalytics\LeadTaxonomy):
     *   lead_form_view             the form is at least 25 % in the viewport (once per form element)
     *   lead_form_started          first trusted focus / input / change on a business field (once per form)
     *   lead_form_validation_error jQuery Validate "invalid-form" on submit; one event per field + reason per form
     *   lead_form_submitted        explicit: leadFormSubmitted(form) from the submit handler, after validation passed
     * Only the data-analytics-lead-* values, the input NAME (allowlist) and the failing rule category
     * are read - never a field value.
     */
    function leadDimensions(form) {
        const dims = {
            form_id: cleanId(form.getAttribute('data-analytics-lead-form') || ''),
            placement: cleanId(form.getAttribute('data-analytics-lead-placement') || ''),
            lead_type: cleanId(form.getAttribute('data-analytics-lead-type') || ''),
            lead_source: cleanId(form.getAttribute('data-analytics-lead-source') || ''),
            form_variant: cleanId(form.getAttribute('data-analytics-lead-variant') || ''),
            form_type: cleanId(form.getAttribute('data-analytics-lead-form-type') || ''),
            page_slug: cleanId(form.getAttribute('data-analytics-lead-page') || ''),
        };

        Object.keys(dims).forEach((key) => {
            if (dims[key] === null) {
                delete dims[key];
            }
        });

        return dims;
    }

    function isLeadBusinessField(field) {
        if (!field.matches('input, select, textarea') || field.closest('[data-analytics-ignore]')) {
            return false;
        }

        const type = (field.getAttribute('type') || '').toLowerCase();
        const name = field.getAttribute('name') || '';

        // Hidden technical fields, buttons and the honeypot (name="website", tabindex="-1") never count.
        return !['hidden', 'submit', 'button', 'reset', 'image'].includes(type) && name !== 'website' && field.tabIndex !== -1;
    }

    function onUserGesture(event) {
        if (event.isTrusted) {
            lastUserGesture = now();
        }
    }

    function onLeadInteraction(event) {
        try {
            if (!event.isTrusted || !(event.target instanceof win.Element)) {
                return; // script-dispatched input / change, prefill, plugin initialisation
            }

            // element.focus() from a script also produces a trusted focusin: a focus only counts
            // right after a real pointer / keyboard gesture. Typing and autofill (input / change) always count.
            if (event.type === 'focusin' && (lastUserGesture === null || now() - lastUserGesture > 1000)) {
                return;
            }

            const form = event.target.closest(LEAD_FORM_SELECTOR);

            if (form === null || startedLeadForms.has(form) || !isLeadBusinessField(event.target)) {
                return;
            }

            startedLeadForms.add(form);

            const dims = leadDimensions(form);
            track('lead_form_started', { form_id: dims.form_id, placement: dims.placement, lead_type: dims.lead_type, page_slug: dims.page_slug });
        } catch (e) {
            // ignore
        }
    }

    function leadErrorField(element) {
        const name = element && element.getAttribute ? element.getAttribute('name') || '' : '';

        if (name.indexOf('services') === 0) {
            return 'services';
        }

        return LEAD_ERROR_FIELDS.includes(name) ? name : 'other';
    }

    function leadErrorReason(method) {
        if (method === 'required') {
            return 'required';
        }

        if (method === 'minlength' || method === 'phonedigits') {
            return 'too_short';
        }

        if (method === 'maxlength') {
            return 'too_long';
        }

        return ['email', 'emailCorrectFormat', 'number', 'digits', 'phoneSK', 'phonechars'].includes(method) ? 'invalid_format' : 'invalid';
    }

    function onLeadInvalid(form, validator) {
        try {
            const seen = leadErrorsSeen.get(form) || new Set();
            leadErrorsSeen.set(form, seen);

            const dims = leadDimensions(form);
            const errors = validator && Array.isArray(validator.errorList) ? validator.errorList : [];

            errors.forEach((error) => {
                const field = leadErrorField(error.element);
                const reason = leadErrorReason(error.method);
                const key = `${field}:${reason}`;

                // Noise protection: the same error of the same form view counts once.
                if (seen.has(key) || seen.size >= 20) {
                    return;
                }

                seen.add(key);
                track('lead_form_validation_error', { form_id: dims.form_id, field, reason, page_slug: dims.page_slug });
            });
        } catch (e) {
            // ignore
        }
    }

    function bindLeadValidation(form) {
        if (boundJQuery === null) {
            return;
        }

        // jQuery Validate uses triggerHandler('invalid-form'): it does not bubble, so bind per form.
        boundJQuery(form)
            .off('invalid-form.journeyAnalytics')
            .on('invalid-form.journeyAnalytics', (event, validator) => onLeadInvalid(form, validator));
    }

    function trackLeadView(form) {
        track('lead_form_view', leadDimensions(form));
    }

    function scanLeadForms() {
        try {
            doc.querySelectorAll(LEAD_FORM_SELECTOR).forEach((form) => {
                if (seenLeadForms.has(form)) {
                    return;
                }

                seenLeadForms.add(form);
                bindLeadValidation(form);

                if (typeof win.IntersectionObserver === 'function') {
                    leadObserver = leadObserver || new win.IntersectionObserver((entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                leadObserver.unobserve(entry.target);
                                trackLeadView(entry.target);
                            }
                        });
                    }, { threshold: 0.25 });
                    leadObserver.observe(form);
                } else {
                    trackLeadView(form);
                }
            });
        } catch (e) {
            // ignore
        }
    }

    /**
     * Call right before sending a lead form whose browser validation passed. Tracks
     * lead_form_submitted and returns the analytics-only request fields for the server-side lead
     * events (ja_lead_submit_id / ja_lead_form / ja_lead_placement / ja_lead_source / ja_lead_variant / ja_lead_page).
     * {} when analytics is off or the form is not a marked lead form. Never throws.
     */
    function leadFormSubmitted(form) {
        try {
            const element = form && form.jquery ? form[0] : form;

            if (stopped || !element || !element.matches || !element.matches(LEAD_FORM_SELECTOR)) {
                return {};
            }

            const submitId = generateEventUuid(win.crypto);
            const dims = leadDimensions(element);

            track('lead_form_submitted', { ...dims, submit_id: submitId }, { priority: true });

            const params = { ja_lead_submit_id: submitId };
            const names = { form_id: 'ja_lead_form', placement: 'ja_lead_placement', lead_source: 'ja_lead_source', form_variant: 'ja_lead_variant', page_slug: 'ja_lead_page' };

            Object.keys(names).forEach((key) => {
                if (dims[key] !== undefined) {
                    params[names[key]] = dims[key];
                }
            });

            return params;
        } catch (e) {
            return {};
        }
    }

    // ------------------------------------------------------------ modals (jquery.modal)

    function rememberCloseInteraction(target) {
        let kind = null;

        if (target.closest('a[rel~="modal:close"], .close-modal')) {
            kind = 'close_button';
        } else if (target.classList && target.classList.contains('blocker')) {
            kind = 'overlay';
        } else {
            const analytics = target.closest('[data-analytics-id]');
            const id = analytics ? cleanId(analytics.getAttribute('data-analytics-id')) : null;

            kind = id !== null ? id : null;
        }

        if (kind !== null) {
            lastCloseInteraction = { time: now(), kind };
        }
    }

    function onKeydown(event) {
        if (event.key === 'Escape') {
            lastCloseInteraction = { time: now(), kind: 'escape' };
        }
    }

    function modalElementOf(event, context) {
        const fromContext = context && context.elm ? (context.elm.jquery ? context.elm[0] : context.elm) : null;

        return fromContext || (event.target instanceof win.Element ? event.target : null);
    }

    function modalKeyOf(element) {
        if (!element) {
            return 'unidentified';
        }

        const marker = element.matches('[data-analytics-modal]') ? element : element.querySelector('[data-analytics-modal]');
        const declared = marker ? cleanId(marker.getAttribute('data-analytics-modal')) : null;

        if (declared !== null) {
            return declared;
        }

        const id = cleanId(element.id || '');

        // modal_<Date.now()> and similar generated ids are not stable keys.
        return id !== null && !/\d{6,}/.test(id) ? id : 'unidentified';
    }

    function triggerOf(context) {
        const anchor = context && context.$anchor ? (context.$anchor.jquery ? context.$anchor[0] : context.$anchor) : null;
        const fromAnchor = anchor && anchor.getAttribute ? cleanId(anchor.getAttribute('data-analytics-id') || '') : null;

        if (fromAnchor !== null) {
            return fromAnchor;
        }

        return lastTrigger !== null && now() - lastTrigger.time <= settings.triggerWindowMs ? lastTrigger.id : undefined;
    }

    function onModalOpen(event, context) {
        try {
            const element = modalElementOf(event, context);

            if (element) {
                modalOpenedAt.set(element, now());
            }

            track('modal_open', { modal: modalKeyOf(element), trigger: triggerOf(context) });
        } catch (e) {
            // ignore
        }
    }

    function onModalClose(event, context) {
        try {
            const element = modalElementOf(event, context);
            const openedAt = element ? modalOpenedAt.get(element) : undefined;
            const recent = lastCloseInteraction !== null && now() - lastCloseInteraction.time <= settings.closeInteractionMs;

            track('modal_close', {
                modal: modalKeyOf(element),
                trigger: recent ? lastCloseInteraction.kind : 'programmatic',
                open_ms: openedAt !== undefined ? now() - openedAt : undefined,
            });
        } catch (e) {
            // ignore
        }
    }

    /**
     * jquery.modal fires its lifecycle through jQuery.trigger() on the modal element; those events
     * bubble to document through jQuery only (not to native listeners), so we bind with the same
     * jQuery instance the plugin uses. modal:before-close fires synchronously while the modal is
     * still attached (modal:after-close comes after the fade, when some AJAX modals are already removed).
     */
    function attachJQuery(jq) {
        try {
            if (stopped || typeof jq !== 'function' || typeof jq.modal !== 'function') {
                return false;
            }

            jq(doc)
                .off('.journeyAnalytics')
                .on('modal:open.journeyAnalytics', onModalOpen)
                .on('modal:before-close.journeyAnalytics', onModalClose);

            boundJQuery = jq;
            doc.querySelectorAll(LEAD_FORM_SELECTOR).forEach(bindLeadValidation);

            return true;
        } catch (e) {
            return false;
        }
    }

    // ------------------------------------------------------------ lifecycle

    function start() {
        if (started) {
            return;
        }

        started = true;

        try {
            // Capture phase: runs even when a handler calls stopPropagation(); passive, never preventDefault().
            doc.addEventListener('click', onClick, { capture: true, passive: true });
            doc.addEventListener('keydown', onKeydown, { capture: true, passive: true });
            doc.addEventListener('pointerdown', onUserGesture, { capture: true, passive: true });
            doc.addEventListener('keydown', onUserGesture, { capture: true, passive: true });
            doc.addEventListener('focusin', onLeadInteraction, { capture: true, passive: true });
            doc.addEventListener('input', onLeadInteraction, { capture: true, passive: true });
            doc.addEventListener('change', onLeadInteraction, { capture: true, passive: true });
            doc.addEventListener('change', onProductToggle, { capture: true, passive: true });
            // Lead forms can arrive with lazily rendered page blocks.
            doc.addEventListener('lazy-page-block:loaded', scanLeadForms);
            // Bubble phase on purpose (after the form's own validation handlers), see onSubmit().
            doc.addEventListener('submit', onSubmit);
            win.addEventListener('pagehide', flushOnExit);
            doc.addEventListener('visibilitychange', () => {
                if (doc.visibilityState === 'hidden') {
                    flushOnExit();
                }
            });

            const page = config.page || {};

            track('page_view', {
                page_type: typeof page.type === 'string' ? page.type : undefined,
                page_id: Number.isInteger(page.id) ? page.id : undefined,
                form_type: typeof page.form_type === 'string' ? page.form_type : undefined,
                navigation_type: navigationType(),
            }, { referrer: true, priority: true });

            scanLeadForms();
            trackCheckoutStep();
        } catch (e) {
            // ignore
        }
    }

    return {
        enabled: true,
        start,
        track,
        flush() {
            try {
                pump();
            } catch (e) {
                // ignore
            }
        },
        attachJQuery,
        leadFormSubmitted,
        scanLeadForms,
        state() {
            return { enabled: !stopped, handshakeDone, queued: queue.length, inflight };
        },
    };
}

function readConfig(doc) {
    try {
        const element = doc.getElementById(CONFIG_ELEMENT_ID);

        return element ? JSON.parse(element.textContent || 'null') : null;
    } catch (e) {
        return null;
    }
}

/**
 * Creates and starts the page's single tracker. Idempotent: a second call (double import,
 * re-run of the entry script) returns the existing instance and sends no second page_view.
 */
export function initJourneyAnalytics(win = globalThis.window, settings = undefined) {
    try {
        if (!win) {
            return createNoopTracker();
        }

        if (win[GLOBAL_KEY]) {
            return win[GLOBAL_KEY];
        }

        const tracker = createJourneyTracker(readConfig(win.document), { window: win, settings });
        win[GLOBAL_KEY] = tracker;

        // Minimal global facade for inline Blade scripts and lazy-loaded block scripts, which
        // cannot import ES modules. Only track() and leadFormSubmitted() (standalone lead form scripts, e.g. the SMS
        // opt-out form) are exposed.
        try {
            Object.defineProperty(win, 'JourneyAnalytics', {
                value: Object.freeze({
                    track: (event, metadata) => tracker.track(event, metadata),
                    leadFormSubmitted: (form) => tracker.leadFormSubmitted(form),
                }),
                configurable: true,
            });
        } catch (e) {
            // ignore
        }

        tracker.start();

        return tracker;
    } catch (e) {
        return createNoopTracker();
    }
}

/** ES-module facade over the page tracker (no-op until initJourneyAnalytics() ran / when disabled). */
export const JourneyAnalytics = Object.freeze({
    track(event, metadata = {}) {
        const tracker = globalThis.window ? globalThis.window[GLOBAL_KEY] : null;

        return tracker ? tracker.track(event, metadata) : false;
    },

    /**
     * Lead form submit (browser validation passed): tracks lead_form_submitted and returns the
     * analytics-only request fields ({} when off). See createJourneyTracker().leadFormSubmitted.
     */
    leadFormSubmitted(form) {
        const tracker = globalThis.window ? globalThis.window[GLOBAL_KEY] : null;

        return tracker ? tracker.leadFormSubmitted(form) : {};
    },

    /** True when the page tracker runs (Blade config present, server not switched off). */
    isEnabled() {
        const tracker = globalThis.window ? globalThis.window[GLOBAL_KEY] : null;

        return Boolean(tracker && tracker.enabled && tracker.state().enabled);
    },
});
